// src/components/Homepage.js
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import { AuthContext } from '../context/AuthContext';
import { ADMIN_EMAIL } from '../services/auth';
import Footer from './Footer';
import HowItWorks from './HowItWorks';
import '../style/Homepage.css';
import tarotSpreadCraft from '../assets/images/tarot-spread-craft.jpg';
import tarotSpreadKeith from '../assets/images/tarot-spread-keith.jpg';
import { useI18n } from '../i18n/I18nContext';

const Homepage = () => {
  const { user, userType } = useContext(AuthContext);
  const { t } = useI18n();
  const isAdmin = userType === 'admin'
    || String(user?.email || '').trim().toLowerCase() === ADMIN_EMAIL;
  const dashboardPath = isAdmin
    ? '/admin-login'
    : userType === 'teller'
      ? '/teller-dashboard'
      : '/user-dashboard';
  const dashboardLabel = isAdmin ? t('nav.admin') : t('nav.dashboard');
  const categories = t('home.categories');
  const trustItems = t('home.trust');
  return (
    <div>
      <Header />
      <main className="main app-landing">
        <section className="app-hero">
          <img src={tarotSpreadCraft} alt={t('home.heroAlt')} className="hero-bg-image" />
          <div className="hero-overlay" />
          <div className="app-hero-copy">
            <p className="eyebrow">{t('home.eyebrow')}</p>
            <h1>{t('home.title')}</h1>
            <p className="hero-lead">{t('home.lead')}</p>
            <div className="hero-actions">
              <Link to="/register" className="primary-button">{t('home.create')}</Link>
              <Link to="/how-it-works" className="secondary-button">{t('home.how')}</Link>
            </div>
            <div className="hero-metrics" aria-label={t('a11y.appHighlights')}>
              <div><strong>3 min</strong><span>{t('home.metricReader')}</span></div>
              <div><strong>{t('home.holdValue')}</strong><span>{t('home.metricHold')}</span></div>
              <div><strong>5 min</strong><span>{t('home.metricRoom')}</span></div>
            </div>
          </div>

          <figure className="hero-reading-visual">
            <div className="hero-spread-photo">
              <img src={tarotSpreadCraft} alt={t('home.spreadAlt')} />
              <span className="hero-visual-kicker">{t('home.visualKicker')}</span>
            </div>
          </figure>
        </section>

        <section className="category-strip" aria-label={t('a11y.readingCategories')}>
          {categories.map((category) => (
            <Link to="/find-teller" key={category}>{category}</Link>
          ))}
        </section>

        <section className="spread-story-band" aria-labelledby="spread-story-title">
          <div className="spread-story-image">
            <img src={tarotSpreadKeith} alt={t('home.storyImageAlt')} />
          </div>
          <div className="spread-story-copy">
            <p className="eyebrow">{t('home.storyEyebrow')}</p>
            <h2 id="spread-story-title">{t('home.storyTitle')}</h2>
            <p>{t('home.storyBody')}</p>
            <Link to="/content-library" className="secondary-button">{t('home.storyCta')}</Link>
          </div>
        </section>

        <HowItWorks />

        <section className="feature-grid-section">
          <div className="section-heading">
            <p className="eyebrow">{t('home.webEyebrow')}</p>
            <h2>{t('home.webTitle')}</h2>
            <p>{t('home.webBody')}</p>
          </div>

          <div className="feature-grid">
            {trustItems.map(([title, body]) => (
              <article className="feature-card" key={title}>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="split-band">
          <div>
            <p className="eyebrow">{t('home.clients')}</p>
            <h2>{t('home.clientTitle')}</h2>
          </div>
          <p>
            {t('home.clientBody')}
          </p>
          <Link to={user ? dashboardPath : "/login"} className="primary-button">{user ? dashboardLabel : t('nav.login')}</Link>
        </section>

        <section className="content-band">
          <div className="section-heading">
            <p className="eyebrow">{t('home.learn')}</p>
            <h2>{t('home.learnTitle')}</h2>
            <p>{t('home.learnBody')}</p>
          </div>
          <Link to="/content-library" className="secondary-button">{t('home.openLibrary')}</Link>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Homepage;
